import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { BaseModal } from '@repo/components/modals';
import emailIsValid from '@repo/utils/helpers/valid-email';
import { apiRequest } from '@repo/utils/helpers/api';
import TextInput from '@components/Login/TextInput';
import Button from '@components/Button';
import { setVerifyEmailRedirect } from '@repo/utils/helpers/verify-email-redirect';
import { LoaderIcon } from '@repo/icons';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { supportEmailProps } from '@repo/utils/helpers/preferred-email-client';
import styles from './AuthAccounts.module.scss';

type LoginEmailStatus = 'sent' | 'connected_account' | 'verified';
interface LoginEmail {
  email: string;
  status: LoginEmailStatus;
}
type AddEmailModalStatus = LoginEmailStatus | 'user_entering' | 'error';

export default function AddEmailModal({ open, closeModal }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<AddEmailModalStatus>('user_entering');
  const [saving, setSaving] = useState(false);

  const restart = () => {
    setEmail('');
    setStatus('user_entering');
  };

  async function verifyEmail(): Promise<void> {
    setSaving(true);
    try {
      const emailAddition = await apiRequest<LoginEmail>(
        'POST',
        '/verified-emails/add-email/',
        { email },
        { apiVersion: 'v2' },
      );

      if (emailAddition.status === 'sent') {
        setVerifyEmailRedirect('/settings/auth');
      }

      setSaving(false);
      setEmail(emailAddition.email);
      setStatus(emailAddition.status || 'sent');
    } catch (e) {
      setSaving(false);
      setStatus('error');
      console.error(e);
    }
  }

  const addingEmailUI = (
    <div className="modal-content-padded">
      <TextInput
        className={styles.input}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onEnter={verifyEmail}
        placeholder="Enter your email"
        validateValue={emailIsValid}
        invalidValueMsg="Please enter a valid email"
        autoFocus
      />
      <small className="text-secondary">We'll send a verification email to this address.</small>
      <div className={styles.btnRow}>
        <Button className="btn btn-muted" onClick={closeModal}>
          {saving ? 'Close' : 'Cancel'}
        </Button>
        <Button
          className={cn('btn btn-primary', styles.btn)}
          disabled={!emailIsValid(email)}
          onClick={verifyEmail}
        >
          {saving ? (
            <>
              Saving <LoaderIcon />
            </>
          ) : (
            <>Add Email</>
          )}
        </Button>
      </div>
    </div>
  );

  const errorUI = (
    <div className="modal-content-padded">
      <p>
        Sorry, something went wrong when we tried to add <b>{email}</b>. Please try again or{' '}
        <a {...supportEmailProps({ subject: 'Issue adding sign in email' })}>
          reach out for assistance
        </a>
        .
      </p>
      <div className={styles.btnRow}>
        <Button className="btn btn-muted" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          className={cn('btn btn-primary', styles.btn)}
          onClick={() => setStatus('user_entering')}
        >
          Try again
        </Button>
      </div>
    </div>
  );

  const verificationEmailSentUI = (
    <div className="modal-content-padded">
      <p>
        We've sent an email to <b>{email}</b>. Please check your inbox. Once you've clicked the
        verification link in that email, you'll see <b>{email}</b> appear under{' '}
        <b>Sign in Accounts</b>.
      </p>
      <div className={styles.btnRow}>
        <Button className="btn btn-muted" onClick={closeModal}>
          Close
        </Button>
        <Button className={cn('btn btn-primary', styles.btn)} onClick={restart}>
          Add another email
        </Button>
      </div>
    </div>
  );

  const emailAlreadyVerifiedUI = (
    <div className="modal-content-padded">
      <p>
        It looks like <b>{email}</b> has already been added as a verified Clay sign in option!
      </p>
      <div className={styles.btnRow}>
        <Button className="btn btn-muted" onClick={closeModal}>
          Close
        </Button>
        <Button className={cn('btn btn-primary', styles.btn)} onClick={restart}>
          Add a different email
        </Button>
      </div>
    </div>
  );

  const emailIsAConnectedAccountUI = (
    <div className="modal-content-padded">
      <p>
        You've already connected <b>{email}</b> to Clay. Though you won't see this email appear
        under <b>Sign in Accounts</b>, you can still use it to sign in to Clay via email.
      </p>
      <div className={styles.btnRow}>
        <Button className="btn btn-muted" onClick={closeModal}>
          Close
        </Button>
        <Button className={cn('btn btn-primary', styles.btn)} onClick={restart}>
          Add a different email
        </Button>
      </div>
    </div>
  );

  useEffect(() => {
    setEmail('');
  }, [open]);

  return (
    <BaseModal
      className={cn('modal-small', styles.AddEmailModal)}
      isOpen={open}
      onRequestClose={closeModal}
    >
      <div className="modal-header">Add Sign In Email</div>
      <StateBasedFadeTransition
        state={status}
        elementByStateMap={{
          user_entering: addingEmailUI,
          error: errorUI,
          sent: verificationEmailSentUI,
          verified: emailAlreadyVerifiedUI,
          connected_account: emailIsAConnectedAccountUI,
        }}
      />
    </BaseModal>
  );
}
